import React, { useState } from 'react';

import { Button } from 'components/ui/button';
import { Errors } from 'components/ui/errors';
import { Field } from '@buildoutinc/blueprint-react/ui/Field';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { messageFor } from 'components/ai/phone_enrollment/helpers';
import { post } from 'helpers/api';

import type { EmailEnrollment } from 'components/users/types';

interface Props {
  address: string;
  path: string;
  onClose: () => void;
  onEnrolled: (enrollment: EmailEnrollment) => void;
}

export function EmailEnrollmentDialog({ address: initialAddress, path, onClose, onEnrolled }: Props) {
  const [address, setAddress] = useState(initialAddress);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function sendConfirmation() {
    setBusy(true);
    setError(null);

    try {
      const pending: EmailEnrollment = await post(path, { address });
      onEnrolled(pending);
    } catch (thrown) {
      setError(messageFor(thrown));
    } finally {
      setBusy(false);
    }
  }

  // Closing mid-request would hide whatever it comes back with.
  function handleClose() {
    if (!busy) {
      onClose();
    }
  }

  return (
    <Modal open onClose={handleClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Set up email</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Field className="form-group">
            <Field.Label htmlFor="emailEnrollmentAddress">Your email address</Field.Label>
            <Input
              id="emailEnrollmentAddress"
              type="email"
              value={address}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAddress(event.target.value)}
            />
            <Field.Description>
              We&apos;ll email a link to this address.
            </Field.Description>
          </Field>

          <Errors error={error} />
        </Modal.Body>

        <Modal.Footer>
          <Button disabled={busy} label="Cancel" variant="ghost" onClick={onClose} />
          <Button disabled={busy || !address.includes('@')} label="Send confirmation" onClick={sendConfirmation} />
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
